import { useMemo } from 'react'

import { Box, List, SearchField, Section } from '@factorialco/deprecated-design-system'
import { OneEmptyState } from '@factorialco/f0-react/dist/experimental'
import { CompensationsPayrollConceptsDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import helpCenterUrl from 'modules/core/lib/helpCenterUrl'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'

import { isFeatureEnabled } from 'modules/compensations/lib/utils'
import CategoryBlock from 'modules/compensations/pages/settings/PayrollConcepts/CategoryBlock'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import {
  PAYROLL_CONCEPT_CATEGORIES,
  UNCATEGORIZED_CATEGORY,
  payrollConceptCategoriesLabels,
} from 'modules/payroll_bundle/lib/constants'

const PayrollConcepts = () => {
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const { loading, data, error } = useQuery(CompensationsPayrollConceptsDocument, {
    variables: {
      companyId,
    },
  })

  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const concepts = useMemo(() => data?.payroll.concepts || [], [data?.payroll.concepts])

  const filteredConcepts = useMemo(() => {
    if (!debouncedSearch.trim()) return concepts
    const categoryLabels = payrollConceptCategoriesLabels()
    return concepts.filter((concept) => {
      if (matchText(debouncedSearch, concept.label)) return true
      const categoryLabel = concept.category ? categoryLabels[concept.category] : undefined
      return categoryLabel ? matchText(debouncedSearch, categoryLabel) : false
    })
  }, [concepts, debouncedSearch])

  const filteredUncategorizedConcepts = useMemo(
    () => filteredConcepts.filter((concept) => !concept.category),
    [filteredConcepts]
  )

  const canMergeConcepts = isFeatureEnabled(
    'dev_compensations_payroll_concept_merge',
    data?.features?.featuresConnection
  )

  return (
    <ResponsivePage.Body loading={loading} error={error}>
      <Section.FullWidth
        illustration="settings"
        title={i18n.t('compensations.settings.concepts.section.title')}
        description={i18n.t('compensations.settings.concepts.section.full_description')}
        link={{
          href: helpCenterUrl('compensations_payroll_concepts'),
          label: i18n.t('compensations.settings.help'),
        }}
        size="600"
      >
        {concepts.length > 0 && (
          <>
            <Box paddingBottom="s16">
              <SearchField
                variant="big"
                search={search}
                onSearch={onSearch}
                placeholder={i18n.t('compensations.settings.concepts.search_placeholder')}
                loading={search !== debouncedSearch}
              />
            </Box>
            {debouncedSearch.trim() && filteredConcepts.length === 0 ? (
              <Box justifyContent="center" alignItems="center" paddingY="s64">
                <OneEmptyState
                  title={i18n.t('compensations.payroll_run.search.no_results.title')}
                  description={i18n.t('compensations.payroll_run.search.no_results.description')}
                  emoji="🔍"
                />
              </Box>
            ) : (
              <List>
                {PAYROLL_CONCEPT_CATEGORIES.map((category) => {
                  const categoryConcepts = filteredConcepts.filter(
                    (concept) => concept.category === category
                  )
                  if (debouncedSearch.trim() && categoryConcepts.length === 0) return null
                  return (
                    <CategoryBlock
                      key={category}
                      category={category}
                      concepts={categoryConcepts}
                      canMergeConcepts={canMergeConcepts}
                    />
                  )
                })}
                {filteredUncategorizedConcepts.length > 0 && (
                  <CategoryBlock
                    key={UNCATEGORIZED_CATEGORY}
                    category={UNCATEGORIZED_CATEGORY}
                    concepts={filteredUncategorizedConcepts}
                    canMergeConcepts={canMergeConcepts}
                  />
                )}
              </List>
            )}
          </>
        )}
      </Section.FullWidth>
    </ResponsivePage.Body>
  )
}

export default PayrollConcepts
